import React, { useState, useEffect, useCallback } from "react";
import { Smartphone, Ship, MapPin, RefreshCw, CheckCircle, Phone } from "lucide-react";
import QRCode from "qrcode";
import { get, post, put, del } from "../services/api";
import CallModal from "../components/CallModal";

interface Device {
  id: string;
  vessel_id: string;
  vessel_name: string;
  device_name: string;
  device_token: string;
  phone_number: string | null;
  management_number: string | null;
  is_active: number;
  registered_at: string;
  last_seen: string | null;
  last_lat: number | null;
  last_lon: number | null;
  /** 充電残量（0〜100）。端末が対応していない・まだ送ってきていない場合は null */
  last_battery: number | null;
  last_battery_charging: number | null;
  last_battery_at: string | null;
}

/** 充電残量の表示（残量が少ないほど赤くする。船内で電池切れになると打刻ができなくなる） */
function batteryStyle(pct: number): { bg: string; fg: string; bd: string; icon: string } {
  if (pct <= 15) return { bg: "#fef2f2", fg: "#dc2626", bd: "#fecaca", icon: "🪫" };
  if (pct <= 30) return { bg: "#fffbeb", fg: "#b45309", bd: "#fde68a", icon: "🪫" };
  return { bg: "#f0fdf4", fg: "#15803d", bd: "#bbf7d0", icon: "🔋" };
}

interface Vessel {
  id: string;
  vessel_name: string;
}

/**
 * アルコール検知器の使用回数。
 * AN-S126a はセンサー寿命1000回の使い捨てで、検知器自体に固有IDが無い。
 * そのため「どの端末に挿さっている検知器か」を単位として数えている。
 */
interface AlcoholDetector {
  device_id: string;
  serial_no: string | null;
  measurement_count: number;
  max_count: number;
  remaining: number;
  status: 'ok' | 'soon' | 'over';
  last_measured_at: string | null;
}

/** 残りが少ないほど赤くする。洋上では交換品の手配に時間がかかるため早めに気づけるようにする */
function detectorStyle(st: AlcoholDetector['status']): { bg: string; fg: string; bd: string; icon: string } {
  if (st === 'over') return { bg: '#fef2f2', fg: '#dc2626', bd: '#fecaca', icon: '🚫' };
  if (st === 'soon') return { bg: '#fffbeb', fg: '#b45309', bd: '#fde68a', icon: '⚠️' };
  return { bg: '#f0fdf4', fg: '#15803d', bd: '#bbf7d0', icon: '🧪' };
}

// SQLite の datetime('now') は "YYYY-MM-DD HH:MM:SS"(UTC・タイムゾーン無し)。
// JSはこれをローカル時刻として解釈するため約9時間ずれ、直近接続でも「9時間前/オフライン」に
// なってしまう。明示的にUTCとして解釈してからミリ秒に変換する。
function parseTs(iso: string | null): number {
  if (!iso) return NaN;
  const s = iso.includes("T") ? iso : iso.replace(" ", "T");
  const withZ = /[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s + "Z";
  return new Date(withZ).getTime();
}

function relativeTime(iso: string | null): string {
  const t = parseTs(iso);
  if (isNaN(t)) return "未接続";
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "たった今";
  if (mins < 60) return `${mins}分前`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}時間前`;
  return `${Math.floor(hrs / 24)}日前`;
}

function isOnline(iso: string | null): boolean {
  const t = parseTs(iso);
  if (isNaN(t)) return false;
  return Date.now() - t < 5 * 60 * 1000;
}

function EditModal({
  device, vessels, onClose, onSaved,
}: { device: Device; vessels: Vessel[]; onClose: () => void; onSaved: () => void }) {
  const [vesselId, setVesselId] = useState(device.vessel_id);
  const [deviceName, setDeviceName] = useState(device.device_name);
  const [phoneNumber, setPhoneNumber] = useState(device.phone_number ?? "");
  const [mgmtNumber, setMgmtNumber] = useState(device.management_number ?? "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    // 登録船舶を変更する場合は警告（この端末はすでに別の船舶に紐づいているため）
    if (vesselId !== device.vessel_id) {
      const fromName = device.vessel_name || "現在の船舶";
      const toName = vessels.find(v => v.id === vesselId)?.vessel_name || "別の船舶";
      const ok = window.confirm(
        `この端末の登録船舶を変更します。\n\n「${fromName}」→「${toName}」\n\n` +
        `この端末で取得するGPS位置情報や乗船情報の紐づけ先が変わります。よろしいですか？`
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      await put(`/devices/${device.id}`, { vessel_id: vesselId, device_name: deviceName, phone_number: phoneNumber, management_number: mgmtNumber });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div style={{ background: "white", borderRadius: 16, padding: 28, width: 420, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>デバイス編集</div>
          <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 20, color: "#94a3b8", cursor: "pointer" }}>✕</button>
        </div>
        <label style={{ display: "block", marginBottom: 14 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#475569", display: "block", marginBottom: 6 }}>船舶</span>
          <select value={vesselId} onChange={e => setVesselId(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14 }}>
            {vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
          </select>
        </label>
        <label style={{ display: "block", marginBottom: 14 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#475569", display: "block", marginBottom: 6 }}>デバイス名</span>
          <input value={deviceName} onChange={e => setDeviceName(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, boxSizing: "border-box" }} />
        </label>
        <label style={{ display: "block", marginBottom: 14 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#475569", display: "block", marginBottom: 6 }}>電話番号</span>
          <input value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="例: 090-1234-5678"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, boxSizing: "border-box" }} />
        </label>
        <label style={{ display: "block", marginBottom: 20 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#475569", display: "block", marginBottom: 6 }}>管理番号</span>
          <input value={mgmtNumber} onChange={e => setMgmtNumber(e.target.value)} placeholder="例: TB-001"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 14, boxSizing: "border-box" }} />
        </label>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid #e2e8f0", background: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", color: "#475569" }}>キャンセル</button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: "10px", borderRadius: 10, border: "none", background: saving ? "#93c5fd" : "#0ea5e9", color: "white", fontSize: 14, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer" }}>
            {saving ? "保存中..." : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 端末制限（リモートキオスク） ────────────────────────────────────────────
interface MdmDevice {
  device_id: string; model: string; is_device_owner: number;
  applied_mode: string; desired_mode: string; whitelist: string[];
  note: string; last_seen: string;
  phone_number?: string; management_number?: string;
}

/** 船員の個人スマホ用アプリ（閲覧・勤怠修正・連絡）の配布セクション。QRコードとURLを提示する。 */
function PersonalAppSection() {
  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // 管理サイトのホスト（例: unison-tech.unison-sea.com）から船員アプリURLを導出する
  const crewUrl = (() => {
    const parts = window.location.hostname.split(".");
    if (parts.length >= 3) return `https://${parts[0]}.crew.${parts.slice(1).join(".")}`;
    return `https://${window.location.hostname}`;
  })();
  const personalUrl = `${crewUrl}/?app=personal`; // 個人用（閲覧専用）。iPhone・Android 共通
  const managedUrl  = `${crewUrl}?app=managed`;   // 管理用（フル操作・動作確認/代理）

  useEffect(() => {
    if (!open) return;
    // 保存して配れるよう大きめに作る（表示は240px）
    QRCode.toDataURL(personalUrl, { width: 720, margin: 1 }).then(setQr).catch(() => setQr(null));
  }, [open, personalUrl]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(personalUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };

  return (
    <div style={{ background: "linear-gradient(135deg,#eff6ff,#f0f9ff)", border: "1.5px solid #bfdbfe", borderRadius: 14, padding: "18px 24px", marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1e40af", marginBottom: 4 }}>📱 船員の個人スマホ用アプリを配布</div>
          <div style={{ fontSize: 13, color: "#1e3a8a", lineHeight: 1.6 }}>
            船員が自分のスマホで勤怠の確認・修正・連絡・ヒヤリハット報告を行えます（<strong>打刻は不可</strong>・船の管理端末のみ）。
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
          <a href={managedUrl} target="_blank" rel="noreferrer"
            title="船員アプリの管理用（フル操作）。船員の実際の動き・代理の確認はこちら"
            style={{ padding: "10px 18px", background: "#eef2ff", color: "#4338ca", border: "1px solid #c7d2fe", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
            🖥 管理用アプリを開く
          </a>
          <button onClick={() => setOpen(true)}
            style={{ padding: "10px 22px", background: "#2563eb", color: "white", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14 }}>
            📱 個人アプリQR
          </button>
        </div>
      </div>

      {open && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setOpen(false)}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: 400, maxWidth: "92vw", background: "white", borderRadius: 16, padding: 28, textAlign: "center" }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>📱 個人スマホ用アプリ</div>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>
              船員のスマホ（<strong>iPhone・Android 共通</strong>）でQRを読み取り、開いた画面で「ホーム画面に追加」すると「カツオ勤怠」アイコンから使えます。アプリストアは不要です。<br />
              ログインは社員コード＋パスワードです。
            </div>
            {qr ? (
              <img src={qr} alt="個人アプリQRコード" style={{ width: 240, height: 240, borderRadius: 8, border: "1px solid #e2e8f0" }} />
            ) : (
              <div style={{ width: 240, height: 240, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 13 }}>QR生成中...</div>
            )}
            <div style={{ margin: "14px 0 6px", fontSize: 13, fontWeight: 700, color: "#0f172a", wordBreak: "break-all" }}>{personalUrl}</div>
            {qr && (
              <a href={qr} download="カツオ勤怠_個人スマホ用アプリQR.png"
                style={{ display: "block", margin: "6px 0 10px", padding: "9px", borderRadius: 10, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", fontWeight: 700, fontSize: 13, textDecoration: "none" }}>
                ⬇ QR画像を保存（配布・LINE用）
              </a>
            )}
            <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 12 }}>
              ※個人スマホからは打刻できません（閲覧・勤怠修正・連絡・ヒヤリハット報告のみ）
            </div>
            <div style={{ background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 8, padding: "8px 12px", marginBottom: 16, fontSize: 11.5, color: "#4338ca", wordBreak: "break-all" }}>
              動作確認・代理操作は<strong>管理用</strong>：<a href={managedUrl} target="_blank" rel="noreferrer" style={{ color: "#4338ca", fontWeight: 700 }}>{managedUrl}</a>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={copy}
                style={{ flex: 1, padding: "11px", borderRadius: 10, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                {copied ? "✓ コピーしました" : "URLをコピー"}
              </button>
              <button onClick={() => setOpen(false)}
                style={{ flex: 1, padding: "11px", borderRadius: 10, border: "none", background: "#0f172a", color: "white", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MdmSection() {
  const [mdmDevices, setMdmDevices] = useState<MdmDevice[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = useCallback(() => {
    get<{ data: MdmDevice[] }>("/mdm/devices").then(r => setMdmDevices(r.data ?? [])).catch(() => {});
  }, []);

  useEffect(() => {
    reload();
    const id = setInterval(reload, 30000);
    return () => clearInterval(id);
  }, [reload]);

  const setMode = async (d: MdmDevice, mode: "locked" | "open") => {
    if (mode === "locked" && !confirm(`この端末を制限モードにしますか？\n許可アプリ（${d.whitelist.length}件）以外がホーム画面から消え、アプリの追加・削除も禁止されます。\n反映まで最大1分かかります。`)) return;
    setBusy(d.device_id);
    try {
      await put(`/mdm/devices/${d.device_id}`, { mode });
      reload();
    } catch (e: any) { alert(e?.message ?? "変更に失敗しました"); }
    setBusy(null);
  };

  const saveNote = async (d: MdmDevice, note: string) => {
    try { await put(`/mdm/devices/${d.device_id}`, { note }); } catch { /* ignore */ }
  };
  const saveField = async (d: MdmDevice, patch: Record<string, string>) => {
    try { await put(`/mdm/devices/${d.device_id}`, patch); reload(); } catch { /* ignore */ }
  };

  if (mdmDevices.length === 0) return null;

  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: 24, marginBottom: 28, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", border: "1px solid #ede9fe" }}>
      <h2 style={{ marginBottom: 4, fontSize: 15, fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
        🔒 端末制限（リモートキオスク）
      </h2>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
        CATMS Companion を管理者化した端末を遠隔で制限できます。制限中は許可アプリ（CATMS・カメラ・電卓・設定など）以外が非表示になり、アプリの追加・削除も禁止されます。端末は約60秒ごとに設定を取得します。
      </p>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f8fafc" }}>
            {["端末", "電話番号", "管理番号", "メモ", "管理者化", "現在の状態", "最終通信", "操作"].map(h => (
              <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "#64748b", fontWeight: 600, fontSize: 11, borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {mdmDevices.map(d => {
            const online = isOnline(d.last_seen);
            const locked = d.applied_mode === "locked";
            const pending = d.desired_mode !== d.applied_mode;
            return (
              <tr key={d.device_id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "10px 12px" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>{d.model || "不明な端末"}</div>
                  <div style={{ fontSize: 10, color: "#94a3b8", fontFamily: "monospace" }}>{d.device_id}</div>
                </td>
                <td style={{ padding: "10px 12px" }}>
                  <input defaultValue={d.phone_number ?? ""} placeholder="例: 090-1234-5678" key={`ph-${d.phone_number}`}
                    onBlur={e => { if (e.target.value !== (d.phone_number ?? "")) saveField(d, { phone_number: e.target.value }); }}
                    style={{ width: 130, padding: "5px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                </td>
                <td style={{ padding: "10px 12px" }}>
                  <input defaultValue={d.management_number ?? ""} placeholder="例: TB-001" key={`mn-${d.management_number}`}
                    onBlur={e => { if (e.target.value !== (d.management_number ?? "")) saveField(d, { management_number: e.target.value }); }}
                    style={{ width: 110, padding: "5px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                </td>
                <td style={{ padding: "10px 12px" }}>
                  <input defaultValue={d.note} placeholder="例: 第一ユニゾン丸 備付" key={`nt-${d.note}`}
                    onBlur={e => { if (e.target.value !== d.note) saveNote(d, e.target.value); }}
                    style={{ width: 140, padding: "5px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                </td>
                <td style={{ padding: "10px 12px" }}>
                  {d.is_device_owner
                    ? <span style={{ color: "#16a34a", fontWeight: 700, fontSize: 12 }}>✓ 済み</span>
                    : <span style={{ color: "#d97706", fontWeight: 600, fontSize: 12 }} title="端末側でDevice Owner化が必要です">未（制限不可）</span>}
                </td>
                <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                  <span style={{
                    padding: "3px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                    background: locked ? "#fef2f2" : "#f0fdf4", color: locked ? "#dc2626" : "#16a34a",
                    border: `1px solid ${locked ? "#fecaca" : "#bbf7d0"}`,
                  }}>
                    {locked ? "🔒 制限中" : "🔓 制限なし"}
                  </span>
                  {pending && <span style={{ marginLeft: 6, fontSize: 11, color: "#d97706", fontWeight: 600 }}>⏳ 反映待ち</span>}
                </td>
                <td style={{ padding: "10px 12px", fontSize: 12, color: online ? "#16a34a" : "#94a3b8", whiteSpace: "nowrap" }}>
                  {online ? "● " : "○ "}{relativeTime(d.last_seen)}
                </td>
                <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                  {d.desired_mode === "locked" ? (
                    <button onClick={() => setMode(d, "open")} disabled={busy === d.device_id}
                      style={{ padding: "6px 16px", background: "#f0fdf4", color: "#16a34a", border: "1.5px solid #bbf7d0", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                      制限を解除
                    </button>
                  ) : (
                    <button onClick={() => setMode(d, "locked")} disabled={busy === d.device_id || !d.is_device_owner}
                      style={{ padding: "6px 16px", background: d.is_device_owner ? "#dc2626" : "#e2e8f0", color: d.is_device_owner ? "#fff" : "#94a3b8", border: "none", borderRadius: 8, cursor: d.is_device_owner ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 700 }}>
                      制限をかける
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function DevicesPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [vesselId, setVesselId] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [newToken, setNewToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [editTarget, setEditTarget] = useState<Device | null>(null);
  const [callTarget, setCallTarget] = useState<Device | null>(null);
  const [detectors, setDetectors] = useState<Record<string, AlcoholDetector>>({});

  const reload = useCallback(() => {
    get<{ data: Device[] }>("/devices").then(r => setDevices(r.data)).catch(() => {});
    get<{ data: AlcoholDetector[] }>("/alcohol-devices")
      .then(r => setDetectors(Object.fromEntries(r.data.map(x => [x.device_id, x]))))
      .catch(() => { /* 取得できなければ検知器の欄を出さないだけ */ });
  }, []);

  /** 検知器を新品に交換した（回数を0に戻す。旧機は履歴として残る） */
  const handleReplaceDetector = async (d: Device, det: AlcoholDetector | undefined) => {
    const now = det?.measurement_count ?? 0;
    if (!confirm(`${d.device_name} のアルコール検知器を新品に交換しますか？

現在の使用回数 ${now}回 を 0回 に戻します。
（古い検知器の記録は履歴として残ります）`)) return;
    const serial = prompt("新しい検知器の管理番号があれば入力してください（任意）", "") ?? "";
    try {
      await post(`/alcohol-devices/${d.id}/replace`, { serial_no: serial || null });
      reload();
    } catch { alert("交換の記録に失敗しました"); }
  };

  /** 検知器本体の「測定確認ボタン」で読んだ回数に合わせる */
  const handleSetDetectorCount = async (d: Device, det: AlcoholDetector | undefined) => {
    const input = prompt(
      `${d.device_name} のアルコール検知器の使用回数を入力してください。

検知器本体で測定後に「測定確認ボタン」を押すと、実際の使用回数が表示されます。その値を入れてください。`,
      String(det?.measurement_count ?? 0));
    if (input === null) return;
    const count = Number(input);
    if (!Number.isFinite(count) || count < 0) { alert("0以上の数値を入力してください"); return; }
    try {
      await put(`/alcohol-devices/${d.id}/count`, { count: Math.floor(count) });
      reload();
    } catch { alert("使用回数の更新に失敗しました"); }
  };

  useEffect(() => {
    reload();
    get<{ data: Vessel[] }>("/vessels").then(r => setVessels(r.data)).catch(() => {});
  }, [reload]);

  // 5分ごとにリロードして last_seen を最新化
  useEffect(() => {
    const id = setInterval(reload, 60000);
    return () => clearInterval(id);
  }, [reload]);

  const handleCreate = async () => {
    if (!vesselId) return alert("船舶を選択してください");
    setLoading(true);
    try {
      const res = await post<{ data: { device_token: string } }>("/devices", { vessel_id: vesselId, device_name: deviceName || "未設定" });
      setNewToken(res.data.device_token);
      reload();
      setVesselId("");
      setDeviceName("");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("このデバイスを無効化しますか？")) return;
    await del(`/devices/${id}`);
    setDevices(d => d.filter(x => x.id !== id));
  };

  return (
    <div style={{ padding: 32, maxWidth: 1100, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 8, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>デバイス管理</h1>
      <p style={{ margin: "0 0 28px", fontSize: 14, color: "#64748b" }}>各スマートフォンの船舶紐付けとGPS位置情報を管理します。</p>

      {/* 個人スマホアプリの配布（QRコード） */}
      <PersonalAppSection />

      {/* 端末制限（リモートキオスク）＋電話番号・管理番号の編集（陸上/システム管理者） */}
      <MdmSection />

      {/* トークン発行 */}
      <div style={{ background: "#fff", borderRadius: 14, padding: 24, marginBottom: 28, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
        <h2 style={{ marginBottom: 16, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>デバイストークン発行</h2>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <select value={vesselId} onChange={e => setVesselId(e.target.value)}
            style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", minWidth: 200, fontSize: 14 }}>
            <option value="">船舶を選択</option>
            {vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
          </select>
          <input value={deviceName} onChange={e => setDeviceName(e.target.value)}
            placeholder="デバイス名（任意）"
            style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", minWidth: 200, fontSize: 14 }} />
          <button onClick={handleCreate} disabled={loading}
            style={{ padding: "9px 22px", background: "#0ea5e9", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 14 }}>
            {loading ? "発行中..." : "発行"}
          </button>
        </div>
        {newToken && (
          <div style={{ marginTop: 16, padding: 16, background: "#f0f9ff", borderRadius: 10, border: "1px solid #0ea5e9" }}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: "#0369a1", fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}><CheckCircle size={14} /> トークンが発行されました（スマホの設定画面に入力してください）</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <code style={{ fontSize: 18, letterSpacing: 2, color: "#0f172a", background: "white", padding: "6px 14px", borderRadius: 6, border: "1px solid #bae6fd" }}>{newToken}</code>
              <button onClick={() => { navigator.clipboard.writeText(newToken); alert("コピーしました"); }}
                style={{ padding: "6px 14px", background: "#fff", border: "1px solid #0ea5e9", borderRadius: 6, cursor: "pointer", fontSize: 13, color: "#0369a1", fontWeight: 600 }}>
                コピー
              </button>
            </div>
          </div>
        )}
      </div>

      {/* デバイス一覧 */}
      <div style={{ background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>登録済みデバイス</h2>
          <button onClick={reload} style={{ padding: "6px 14px", border: "1px solid #e2e8f0", borderRadius: 8, background: "white", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#475569", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <RefreshCw size={13} /> 更新
          </button>
        </div>

        {devices.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 14 }}>デバイスがありません</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {devices.map(d => {
              const online = isOnline(d.last_seen);
              const det = detectors[d.id];
              const hasGps = d.last_lat !== null && d.last_lon !== null;
              const mapsUrl = hasGps
                ? `https://www.google.com/maps?q=${d.last_lat},${d.last_lon}`
                : null;

              return (
                <div key={d.id} style={{ border: "1.5px solid #e2e8f0", borderRadius: 12, padding: "16px 20px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", background: "#fafafa" }}>
                  {/* オンライン状態インジケータ */}
                  <div style={{ flexShrink: 0 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 12,
                      background: online ? "#ecfdf5" : "#f1f5f9",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      position: "relative",
                    }}>
                      <Smartphone size={22} />
                      <div style={{
                        position: "absolute", bottom: 2, right: 2,
                        width: 10, height: 10, borderRadius: "50%",
                        background: online ? "#10b981" : "#94a3b8",
                        border: "2px solid white",
                      }} />
                    </div>
                  </div>

                  {/* デバイス情報 */}
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>{d.device_name}</span>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                        background: online ? "#ecfdf5" : "#f1f5f9",
                        color: online ? "#059669" : "#64748b",
                        border: `1px solid ${online ? "#a7f3d0" : "#e2e8f0"}`,
                      }}>
                        {online ? "● オンライン" : "○ オフライン"}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: "#0ea5e9", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}><Ship size={13} /> {d.vessel_name}</div>
                    {(d.phone_number || d.management_number) && (
                      <div style={{ fontSize: 12, color: "#475569", marginTop: 3, display: "flex", flexWrap: "wrap", gap: "2px 12px" }}>
                        {d.phone_number && <span>📞 {d.phone_number}</span>}
                        {d.management_number && <span>🏷 管理番号: {d.management_number}</span>}
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 3 }}>
                      {d.last_battery !== null && d.last_battery !== undefined ? (() => {
                        const b = batteryStyle(d.last_battery);
                        return (
                          <span title={`充電残量（${relativeTime(d.last_battery_at)}時点）${d.last_battery_charging ? '・充電中' : ''}`}
                            style={{
                              fontSize: 12, fontWeight: 800, padding: "2px 9px", borderRadius: 999,
                              background: b.bg, color: b.fg, border: `1px solid ${b.bd}`, whiteSpace: "nowrap",
                            }}>
                            {b.icon} {d.last_battery}%{d.last_battery_charging ? " ⚡充電中" : ""}
                          </span>
                        );
                      })() : (
                        <span title="この端末からはまだ充電残量が届いていません" style={{ fontSize: 11.5, color: "#94a3b8" }}>🔋 残量 —</span>
                      )}
                      {det && (() => {
                        const ds = detectorStyle(det.status);
                        const title = det.status === 'over'
                          ? `検知器が寿命の${det.max_count}回に達しています。測定値が正しく出ない恐れがあるため交換してください`
                          : det.status === 'soon'
                          ? `検知器の残り${det.remaining}回。交換用を手配してください`
                          : `アルコール検知器の使用回数（寿命${det.max_count}回）`;
                        return (
                          <span title={title} style={{
                            fontSize: 12, fontWeight: 800, padding: "2px 9px", borderRadius: 999,
                            background: ds.bg, color: ds.fg, border: `1px solid ${ds.bd}`, whiteSpace: "nowrap",
                          }}>
                            {ds.icon} 検知器 {det.measurement_count}/{det.max_count}回
                            {det.status === 'over' ? "・要交換" : det.status === 'soon' ? `・残り${det.remaining}` : ""}
                          </span>
                        );
                      })()}
                      <span style={{ fontSize: 12, color: "#94a3b8" }}>最終接続: {relativeTime(d.last_seen)}</span>
                    </div>
                  </div>

                  {/* GPS情報 */}
                  <div style={{ minWidth: 180 }}>
                    {hasGps ? (
                      <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "8px 12px" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#15803d", marginBottom: 4, display: "flex", alignItems: "center", gap: 3 }}><MapPin size={11} /> GPS位置情報</div>
                        <div style={{ fontSize: 12, color: "#166534", fontFamily: "monospace" }}>
                          {d.last_lat!.toFixed(5)}, {d.last_lon!.toFixed(5)}
                        </div>
                        <a href={mapsUrl!} target="_blank" rel="noreferrer"
                          style={{ fontSize: 12, color: "#0ea5e9", fontWeight: 600, textDecoration: "none", display: "inline-block", marginTop: 4 }}>
                          Google Mapsで開く →
                        </a>
                      </div>
                    ) : (
                      <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: 12, color: "#94a3b8" }}>GPS未取得</div>
                      </div>
                    )}
                  </div>

                  {/* アルコール検知器（寿命1000回の使い捨てのため、交換時期を把握する） */}
                  <div style={{ minWidth: 180 }}>
                    {det ? (() => {
                      const ds = detectorStyle(det.status);
                      const pct = Math.min(100, Math.round((det.measurement_count / (det.max_count || 1)) * 100));
                      return (
                        <div style={{ background: ds.bg, border: `1px solid ${ds.bd}`, borderRadius: 8, padding: "8px 12px" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: ds.fg, marginBottom: 4 }}>
                            {ds.icon} アルコール検知器
                          </div>
                          <div style={{ fontSize: 12, color: ds.fg, fontWeight: 700 }}>
                            {det.measurement_count} / {det.max_count} 回（残り{det.remaining}）
                          </div>
                          <div style={{ height: 5, borderRadius: 999, background: "#ffffff", border: `1px solid ${ds.bd}`, marginTop: 5, overflow: "hidden" }}>
                            <div style={{ width: `${pct}%`, height: "100%", background: ds.fg }} />
                          </div>
                          {det.serial_no && <div style={{ fontSize: 11, color: ds.fg, marginTop: 4 }}>🏷 {det.serial_no}</div>}
                          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                            <button onClick={() => handleReplaceDetector(d, det)}
                              style={{ fontSize: 11, fontWeight: 700, color: ds.fg, background: "transparent", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}>
                              交換した
                            </button>
                            <button onClick={() => handleSetDetectorCount(d, det)}
                              style={{ fontSize: 11, fontWeight: 600, color: "#64748b", background: "transparent", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}>
                              回数を修正
                            </button>
                          </div>
                        </div>
                      );
                    })() : (
                      <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: 12, color: "#94a3b8" }}>検知器の記録なし</div>
                      </div>
                    )}
                  </div>

                  {/* トークン */}
                  <div style={{ minWidth: 160 }}>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>トークン</div>
                    <code style={{ fontSize: 11, background: "#f1f5f9", padding: "3px 8px", borderRadius: 4, color: "#475569" }}>
                      {d.device_token.slice(0, 18)}...
                    </code>
                  </div>

                  {/* 操作ボタン */}
                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                    <button onClick={() => setCallTarget(d)} title={online ? "この端末に音声通話で呼び出す" : "端末オフライン（アプリ起動時に通話できます）"}
                      style={{ padding: "6px 14px", background: online ? "#dcfce7" : "#f1f5f9", color: online ? "#15803d" : "#94a3b8", border: `1px solid ${online ? "#86efac" : "#e2e8f0"}`, borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 5 }}>
                      <Phone size={13} /> 通話
                    </button>
                    <button onClick={() => setEditTarget(d)}
                      style={{ padding: "6px 14px", background: "#f0f9ff", color: "#0369a1", border: "1px solid #bae6fd", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      編集
                    </button>
                    <button onClick={() => handleDelete(d.id)}
                      style={{ padding: "6px 14px", background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      無効化
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editTarget && (
        <EditModal
          device={editTarget}
          vessels={vessels}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); reload(); }}
        />
      )}

      {callTarget && (
        <CallModal
          deviceId={callTarget.id}
          deviceName={callTarget.device_name || callTarget.vessel_name || "端末"}
          onClose={() => setCallTarget(null)}
        />
      )}
    </div>
  );
}
